import React, { useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { I18n } from 'react-redux-i18n';
import ChatHeader from '../ChatHeader';
import ChatMessage from '../ChatMessage';
import ChatOptions from '../ChatOptions';
import ChatContactForm from '../ChatContactForm';
import ChatConfirmation from '../ChatConfirmation';
import {
  getContent,
  getMenuSections,
  getSectionQuestions,
  getItemDownloadConfig,
  getItemNavigationConfig,
  getTopicMeta,
  resolveBackTarget,
  resolveMenuLabel,
} from '../../utils/contentRenderer';
import { isINESAccount, isINESBranch } from '../../../utils/inesValidator';
import './ChatWindow.scss';

const ChatWindow = ({
  messages,
  isTyping,
  locale,
  currentStep,
  currentSection,
  currentQuestion,
  confirmationTimer,
  contactForm,
  account,
  branch,
  userGroups,
  isContactSubmitting,
  contactError,
  onClose,
  onNavigate,
  onGoBack,
  onSelectMenuOption,
  onSelectQuestion,
  onSubmitContact,
  onStartChat,
  onGoToMenu,
  onGoToContact,
}) => {
  const messagesEndRef = useRef(null);
  const chatMessagesRef = useRef(null);
  const chatWindowRef = useRef(null);
  const isINESAccountCheck = account ? isINESAccount(account) : false;
  const isINESBranchCheck = branch?.name ? isINESBranch(branch.name) : false;
  const isINES = isINESAccountCheck || isINESBranchCheck;

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, currentStep]);

  // Prevent scroll propagation to dashboard (wheel and keyboard)
  useEffect(() => {
    const chatWindowElement = chatWindowRef.current;
    const chatMessagesElement = chatMessagesRef.current;

    const handleWheel = (e) => {
      // Always stop propagation to prevent parent scroll
      e.stopPropagation();

      // If wheel is on the messages area, handle boundary logic
      if (chatMessagesElement && chatMessagesElement.contains(e.target)) {
        const { scrollTop, scrollHeight, clientHeight } = chatMessagesElement;
        const isScrollingDown = e.deltaY > 0;
        const isScrollingUp = e.deltaY < 0;

        const isAtTop = scrollTop <= 0;
        const isAtBottom = scrollTop + clientHeight >= scrollHeight - 1;

        // Prevent default scroll when at boundaries
        if ((isAtTop && isScrollingUp) || (isAtBottom && isScrollingDown)) {
          e.preventDefault();
        }
      } else {
        // If wheel is on header or other areas, prevent scroll completely
        e.preventDefault();
      }
    };

    const handleKeyDown = (e) => {
      const scrollKeys = [
        'ArrowUp',
        'ArrowDown',
        'PageUp',
        'PageDown',
        'Home',
        'End',
        ' ',
      ];

      if (scrollKeys.includes(e.key)) {
        const isInputFocused = ['INPUT', 'TEXTAREA'].includes(
          document.activeElement?.tagName,
        );

        if (!isInputFocused) {
          e.preventDefault();
          e.stopPropagation();

          // Manually scroll the messages area
          if (chatMessagesElement) {
            const scrollAmount =
              e.key === 'PageUp' || e.key === 'PageDown' ? 200 : 40;
            if (
              e.key === 'ArrowDown' ||
              e.key === 'PageDown' ||
              e.key === ' '
            ) {
              chatMessagesElement.scrollTop += scrollAmount;
            } else if (e.key === 'ArrowUp' || e.key === 'PageUp') {
              chatMessagesElement.scrollTop -= scrollAmount;
            } else if (e.key === 'Home') {
              chatMessagesElement.scrollTop = 0;
            } else if (e.key === 'End') {
              chatMessagesElement.scrollTop = chatMessagesElement.scrollHeight;
            }
          }
        }
      }
    };

    if (chatWindowElement) {
      chatWindowElement.addEventListener('wheel', handleWheel, {
        passive: false,
      });
      chatWindowElement.addEventListener('keydown', handleKeyDown, {
        passive: false,
      });

      return () => {
        chatWindowElement.removeEventListener('wheel', handleWheel);
        chatWindowElement.removeEventListener('keydown', handleKeyDown);
      };
    }
  }, []);

  const renderWelcome = () => {
    try {
      const welcomeRef = { step: 'welcome' };
      const welcomeText = getContent(welcomeRef, locale);

      const handleStartClick = () => {
        console.log('🔵 ChatWindow: Botón Comenzar clickeado');
        console.log('🔵 onStartChat prop:', onStartChat);
        if (typeof onStartChat === 'function') {
          onStartChat();
        } else {
          console.error('onStartChat is not a function:', onStartChat);
        }
      };

      return (
        <div className="chat-welcome-wizard">
          {/* <div className="chat-welcome-wizard__avatar">
            <span className="material-symbols-rounded">waving_hand</span>
          </div> */}
          <div className="chat-welcome-wizard__content">
            <p className="chat-welcome-wizard__text">
              {welcomeText.split('\n').map((line, idx) => (
                <span key={idx}>
                  {line}
                  {idx < welcomeText.split('\n').length - 1 && <br />}
                </span>
              ))}
            </p>
            <div className="chat-options">
              <button
                className="chat-options__item chat-options__item--primary"
                onClick={handleStartClick}
              >
                <span className="material-symbols-rounded">arrow_forward</span>
                <span className="chat-options__label">
                  {I18n.t('chatbot.welcome.start')}
                </span>
              </button>
            </div>
          </div>
        </div>
      );
    } catch (error) {
      console.error('Error rendering welcome screen:', error);
      return (
        <div className="chat-error">
          <p>Error loading welcome screen. Please try again.</p>
        </div>
      );
    }
  };

  // Renderizar menú principal
  const renderMenu = () => {
    try {
      const sections = getMenuSections(locale);

      if (!sections || sections.length === 0) {
        console.error('No sections available');
        return (
          <div className="chat-error">
            <p>Error loading menu sections. Please try again.</p>
          </div>
        );
      }

      const options = sections.map((section) => ({
        id: section.id,
        label: section.label,
        icon: 'folder',
      }));

      options.push({
        id: 'contact',
        label: I18n.t('chatbot.menu.contact'),
        icon: 'mail',
        primary: true, // Destacar la opción de contacto
      });

      return (
        <ChatOptions
          options={options}
          onSelect={(option) => {
            if (option.id === 'contact') {
              onGoToContact();
            } else {
              onSelectMenuOption(option.id, option.id);
            }
          }}
        />
      );
    } catch (error) {
      console.error('Error rendering menu:', error);
      return (
        <div className="chat-error">
          <p>Error loading menu. Please refresh the page.</p>
        </div>
      );
    }
  };

  const renderSection = () => {
    if (!currentSection) return null;

    try {
      // Role gate - verificar si el usuario tiene acceso a esta sección
      const topicMeta = getTopicMeta(currentSection);
      if (topicMeta) {
        const groups = userGroups || [];
        const hasRole =
          groups.includes(1) || groups.includes(topicMeta.requiresRole);
        if (!hasRole) {
          return (
            <div>
              <ChatMessage
                message={{
                  id: Date.now(),
                  text: I18n.t('chatbot.informes.deniedMessage'),
                  sender: 'bot',
                  timestamp: new Date().toISOString(),
                }}
                locale={locale}
              />
              <ChatOptions
                options={[
                  {
                    id: 'back',
                    label: I18n.t('chatbot.navigation.back'),
                    icon: 'arrow_back',
                  },
                ]}
                onSelect={() => onGoToMenu()}
              />
            </div>
          );
        }
      }

      const questions = getSectionQuestions(currentSection, locale);

      if (!questions || questions.length === 0) {
        console.error('No questions available for section:', currentSection);
        return (
          <div className="chat-error">
            <p>No questions available for this section.</p>
            <button onClick={onGoToMenu}>Back to menu</button>
          </div>
        );
      }

      const options = questions.map((q) => ({
        id: q.id,
        label: q.label,
        icon: 'help',
      }));

      options.push({
        id: 'back',
        label: I18n.t('chatbot.navigation.back'),
        icon: 'arrow_back',
      });

      return (
        <ChatOptions
          options={options}
          onSelect={(option) => {
            if (option.id === 'back') {
              const backTarget = resolveBackTarget(currentSection);
              if (backTarget.type === 'section') {
                onNavigate('section', backTarget.section, null);
              } else {
                onGoToMenu();
              }
            } else {
              const nav = getItemNavigationConfig(currentSection, option.id);
              if (nav && nav.section && !nav.question) {
                // Navigate into the sub-section AND post the user's choice as a
                // chat bubble. Pass the section id (not the text) so the label is
                // resolved per-locale and the bubble stays translatable.
                onSelectMenuOption(nav.section, nav.section);
              } else {
                onSelectQuestion(option.id, option.id);
              }
            }
          }}
        />
      );
    } catch (error) {
      console.error('Error rendering section:', error);
      return (
        <div className="chat-error">
          <p>Error loading section questions. Please try again.</p>
          <button onClick={onGoToMenu}>Back to menu</button>
        </div>
      );
    }
  };

  const renderAnswer = () => {
    if (!currentSection || !currentQuestion) return null;

    try {
      return (
        <div>
          <ChatOptions
            options={[
              {
                id: 'more',
                label: I18n.t('chatbot.navigation.moreQuestions'),
                icon: 'help',
              },
              {
                id: 'menu',
                label: I18n.t('chatbot.navigation.backToMenu'),
                icon: 'home',
              },
            ]}
            onSelect={(option) => {
              if (option.id === 'more') {
                onNavigate('section', currentSection, null);
              } else {
                onGoToMenu();
              }
            }}
          />
        </div>
      );
    } catch (error) {
      console.error('Error rendering answer:', error);
      return (
        <div className="chat-error">
          <p>Error loading answer. Please try again.</p>
          <button onClick={onGoToMenu}>Back to menu</button>
        </div>
      );
    }
  };

  // Renderizar formulario de contacto
  const renderContact = () => {
    return (
      <ChatContactForm
        onSubmit={({ email, reason }) => onSubmitContact(email, reason)}
        onCancel={onGoToMenu}
        initialEmail={contactForm?.email || ''}
        initialReason={contactForm?.reason || ''}
        isSubmitting={isContactSubmitting}
        error={contactError}
      />
    );
  };

  // Renderizar confirmación de contacto
  const renderContactConfirm = () => {
    return (
      <ChatConfirmation
        timer={confirmationTimer}
        onTimeout={onGoToMenu}
        onBackToMenu={onGoToMenu}
      />
    );
  };

  // Renderizar contenido según el paso actual
  const renderCurrentStep = () => {
    console.log('🔷 renderCurrentStep - currentStep:', currentStep);

    switch (currentStep) {
      case 'welcome':
        return renderWelcome();
      case 'menu':
        return renderMenu();
      case 'section':
        return renderSection();
      case 'answer':
        return renderAnswer();
      case 'contact':
        return renderContact();
      case 'contact_confirm':
        return renderContactConfirm();
      default:
        return renderWelcome();
    }
  };

  // Renderizar mensajes del historial
  const renderMessage = (msg) => {
    try {
      if (!msg || !msg.type) {
        console.warn('Invalid message:', msg);
        return null;
      }

      if (msg.type === 'user') {
        // Para mensajes de usuario, mostrar el label de la opción seleccionada
        let displayText = '';

        if (msg.optionLabel) {
          if (typeof msg.optionLabel === 'object' && msg.optionLabel.key) {
            // Es un objeto con key, buscar traducción
            const key = msg.optionLabel.key;

            // Intentar encontrar la traducción en diferentes namespaces
            const paths = [
              `chatbot.welcome.${key}`,
              `chatbot.menu.${key}`,
              `chatbot.navigation.${key}`,
              `chatbot.${key}`,
            ];

            displayText = key; // fallback por defecto

            for (const path of paths) {
              const translation = I18n.t(path);
              // Verificar que realmente encontró una traducción válida
              // La traducción es válida si existe y no es igual al path ni a la key
              if (translation && translation !== path && translation !== key) {
                displayText = translation;
                break;
              }
            }
          } else if (typeof msg.optionLabel === 'string') {
            // Es un string que es un ID de sección o pregunta
            // Intentar obtener la traducción dinámica
            if (msg.ref.step === 'menu') {
              // Sección del menú; resolveMenuLabel también localiza las
              // sub-secciones ocultas (Informes DISC / Flujo de navegación).
              displayText = resolveMenuLabel(msg.optionLabel, locale);
            } else if (msg.ref.step === 'question') {
              // Es una pregunta
              const questions = getSectionQuestions(
                msg.ref.section || currentSection,
                locale,
              );
              const question = questions.find((q) => q.id === msg.optionLabel);
              displayText = question ? question.label : msg.optionLabel;
            } else {
              displayText = msg.optionLabel;
            }
          }
        } else {
          // Fallback al contenido de la referencia
          displayText = getContent(msg.ref, locale);
        }

        return (
          <div key={msg.id} className="chat-message chat-message--user">
            <div className="chat-message__bubble">{displayText}</div>
          </div>
        );
      } else {
        // Mensaje del bot
        const content = getContent(msg.ref, locale);
        const downloadConfig =
          msg.ref?.step === 'answer'
            ? getItemDownloadConfig(msg.ref?.section, msg.ref?.question)
            : null;
        const navConfig =
          msg.ref?.step === 'answer'
            ? getItemNavigationConfig(msg.ref?.section, msg.ref?.question)
            : null;
        return (
          <div key={msg.id}>
            <ChatMessage
              message={{
                id: msg.id,
                text: content,
                sender: 'bot',
                timestamp: msg.timestamp,
              }}
              locale={locale}
            />
            {downloadConfig && (
              <div className="chat-download">
                <a
                  href={downloadConfig.files[locale] || downloadConfig.files.es}
                  download
                  className="chat-download__button"
                >
                  <span className="material-symbols-rounded">download</span>
                  <span className="chat-download__label">
                    {downloadConfig.label[locale] || downloadConfig.label.es}
                  </span>
                </a>
              </div>
            )}
            {navConfig && (
              <div className="chat-navigate">
                <button
                  className="chat-navigate__button"
                  onClick={() =>
                    onNavigate('answer', navConfig.section, navConfig.question)
                  }
                >
                  <span className="material-symbols-rounded">
                    arrow_forward
                  </span>
                  <span className="chat-navigate__label">
                    {navConfig.label[locale] || navConfig.label.es}
                  </span>
                </button>
              </div>
            )}
          </div>
        );
      }
    } catch (error) {
      console.error('Error rendering message:', error, msg);
      return null;
    }
  };

  // Keep focus on chat window after interactions
  const handleChatClick = (e) => {
    // Don't steal focus from inputs/textareas
    if (!['INPUT', 'TEXTAREA'].includes(e.target.tagName)) {
      chatWindowRef.current?.focus();
    }
  };

  return (
    <div
      className={`chat-window ${isINES ? 'chat-window--ines' : ''}`}
      ref={chatWindowRef}
      tabIndex={-1}
      onClick={handleChatClick}
    >
      <ChatHeader onClose={onClose} isINES={isINES} />

      <div className="chat-messages" ref={chatMessagesRef}>
        {/* Renderizar historial de mensajes */}
        {messages && messages.length > 0 && messages.map(renderMessage)}

        {/* Renderizar contenido del paso actual */}
        {renderCurrentStep()}

        {/* Indicador de escritura */}
        {isTyping && (
          <div className="chat-typing">
            <div className="chat-typing__dots">
              <span></span>
              <span></span>
              <span></span>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>
    </div>
  );
};

ChatWindow.propTypes = {
  messages: PropTypes.array.isRequired,
  isTyping: PropTypes.bool.isRequired,
  locale: PropTypes.string.isRequired,
  currentStep: PropTypes.string.isRequired,
  currentSection: PropTypes.string,
  currentQuestion: PropTypes.string,
  confirmationTimer: PropTypes.number,
  contactForm: PropTypes.shape({
    email: PropTypes.string,
    reason: PropTypes.string,
  }),
  account: PropTypes.object,
  branch: PropTypes.object,
  userGroups: PropTypes.array,
  isContactSubmitting: PropTypes.bool,
  contactError: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onNavigate: PropTypes.func.isRequired,
  onGoBack: PropTypes.func.isRequired,
  onSelectMenuOption: PropTypes.func.isRequired,
  onSelectQuestion: PropTypes.func.isRequired,
  onSubmitContact: PropTypes.func.isRequired,
  onStartChat: PropTypes.func.isRequired,
  onGoToMenu: PropTypes.func.isRequired,
  onGoToContact: PropTypes.func.isRequired,
};

export default ChatWindow;
